/**
 * VoiceService
 *
 * Handles Twilio Voice-related API operations including token generation
 * and initiating in-app calls.
 *
 * This service provides the API layer for voice calling functionality,
 * separating HTTP concerns from state management.
 *
 * @module services/voiceService
 */

import type { definitions } from '@erebus/api-types';
import { erebus } from 'boot/axios';
import { AxiosResponse } from 'axios';
import { ErrorHandlingService } from 'src/services/errorHandlingService';

/**
 * Response from token endpoint
 * Matches Go's ClientCallTokenResponse model
 */
export interface ClientCallTokenResponse {
  /** Twilio access token for Voice SDK */
  token: string;
  /** Token expiration timestamp (ISO 8601 format) */
  expiresAt: string;
}

/** Response from POST /client-call/start (generated from backend Swagger) */
export type StartCallResponse = definitions['pkg_clientCall.StartCallResponse'];

// The hint is optional in the API (Go *int), but the Swagger-2 type generator emits
// every field as a required number. Anchor to the generated schema and loosen only
// the one field the generator can't represent as nullable.
export type StartCallRequest = Omit<
  definitions['pkg_clientCall.StartCallRequestBody'],
  'latestPhoneActivationID'
> & {
  latestPhoneActivationID: number | null;
};

/**
 * Service class for Twilio Voice API operations
 *
 * Provides methods for:
 * - Starting an in-app call (resolves activation + phone number)
 * - Fetching Twilio access tokens for the Voice SDK
 *
 * @example
 * ```typescript
 * // Use with dependency injection (recommended)
 * const voiceService = inject(VoiceServiceKey);
 * if (!voiceService) throw new Error('VoiceService not provided');
 *
 * // Start a call for a case
 * const { activationID, phoneNumber } = await voiceService.startCall({ caseID: 123, latestPhoneActivationID: null });
 *
 * // Get token for the resolved activation
 * const token = await voiceService.fetchToken(activationID);
 * ```
 */
export default class VoiceService {
  private errorHandlingService: ErrorHandlingService;

  /**
   * Create a VoiceService instance
   * @param errorHandlingService - Error handling service for API error parsing
   */
  constructor(errorHandlingService: ErrorHandlingService) {
    this.errorHandlingService = errorHandlingService;
  }

  /**
   * Fetch Twilio access token for voice calls
   *
   * The token is used to initialize the Twilio Device SDK and authenticate
   * the client for making outbound calls.
   *
   * **Rate Limiting:** 10 requests per hour per user
   *
   * @param activationID - ID of the activation record
   * @returns Twilio access token string
   * @throws ApiError if the request fails or activation not found
   * @throws 429 Too Many Requests if rate limit exceeded
   *
   * @example
   * ```typescript
   * const service = new VoiceService();
   * try {
   *   const token = await service.fetchToken(123);
   *   // Use token to initialize Twilio Device
   * } catch (error) {
   *   console.error('Failed to fetch token:', error);
   * }
   * ```
   */
  async fetchToken(activationID: number): Promise<string> {
    this.validateActivationID(activationID);

    try {
      console.info('Fetching Twilio token for activation');

      const response: AxiosResponse<ClientCallTokenResponse> = await erebus.post(
        `/client-call/token`,
        { activationID },
      );

      const { token } = response.data;

      if (!token || token.trim() === '') {
        throw new Error('Received empty or invalid token from server');
      }

      console.debug('Twilio token fetched successfully');
      return token;
    } catch (error) {
      console.error('Error fetching Twilio token:', error);
      throw this.errorHandlingService.parseApiError(error);
    }
  }

  /**
   * Resolve the activation for a new in-app call.
   *
   * The backend reuses the case's latest phone activation when no call has been
   * placed against it, otherwise creates a fresh activation. Returns the resolved
   * activation ID (request a token for it) and the phone number to dial.
   *
   * @param request - caseID and optional latestPhoneActivationID hint
   * @returns Resolved activation ID and phone number to dial
   * @throws ApiError if the request fails or case not found
   */
  async startCall(request: StartCallRequest): Promise<StartCallResponse> {
    if (!Number.isInteger(request.caseID) || request.caseID <= 0) {
      throw new Error('Case ID must be a positive integer');
    }

    try {
      const response: AxiosResponse<StartCallResponse> = await erebus.post(
        '/client-call/start',
        request,
      );

      const { activationID, phoneNumber } = response.data;
      if (!activationID || activationID <= 0) {
        throw new Error('Received invalid activation ID from server');
      }
      if (!phoneNumber || phoneNumber.trim() === '') {
        throw new Error('Received empty phone number from server');
      }
      return { activationID, phoneNumber };
    } catch (error) {
      console.error('Error starting in-app call:', error);
      throw this.errorHandlingService.parseApiError(error);
    }
  }

  /**
   * Validate that an activation ID is provided
   * Helper method for parameter validation
   *
   * @param activationID - ID to validate
   * @throws Error if activation ID is missing or invalid
   */
  private validateActivationID(activationID?: number): void {
    if (activationID === undefined || activationID === null) {
      throw new Error('Activation ID is required');
    }

    if (!Number.isInteger(activationID) || activationID <= 0) {
      throw new Error('Activation ID must be a positive integer');
    }
  }
}
